import React, { useState } from "react";
import { processUploadedDocument } from "../../services/api";

/**
 * PaperDocumentUpload Component
 * 
 * Step 2 of the paper submission wizard.
 * Handles document upload and automatic AI processing when proceeding to next step.
 * 
 * @param {Object} props - Component props
 * @param {Object} props.formData - Current form data
 * @param {Function} props.handleChange - Form field change handler
 * @param {Function} props.onExtractedInfo - Callback when document is successfully processed
 * @param {Object} props.extractedInfo - Previously extracted information (if any)
 * @param {boolean} props.hasExtractedInfo - Whether information has been extracted
 * @param {Function} props.onAutoExtract - Function to trigger automatic extraction (called externally)
 */
const PaperDocumentUpload = ({
  formData,
  handleChange,
  onExtractedInfo,
  extractedInfo,
  hasExtractedInfo,
  onAutoExtract
}) => {
  const [uploadFile, setUploadFile] = useState(formData.uploadedFile || null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStatus, setProcessingStatus] = useState("");
  const [uploadError, setUploadError] = useState("");

  // Sync uploadFile state with formData when component mounts or formData changes
  React.useEffect(() => {
    if (formData.uploadedFile && formData.uploadedFile !== uploadFile) {
      setUploadFile(formData.uploadedFile);
    }
  }, [formData.uploadedFile, formData.uploadedFileName]);

  // Expose the extraction function to parent component
  React.useEffect(() => {
    if (onAutoExtract) {
      onAutoExtract(handleProcessDocument);
    }
  }, [onAutoExtract]);

  /**
   * Handle file selection
   * @param {Event} event - File input change event
   */
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    setUploadFile(file);
    setUploadError("");
    
    // Clear any previously extracted information when a new file is selected
    if (file && hasExtractedInfo) {
      // Clear extracted information in parent component
      onExtractedInfo(null);
    }
    
    if (file) {
      // Validate file type
      const allowedTypes = [
        'application/pdf',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/msword'
      ];
      
      // Validate file size (25MB = 25 * 1024 * 1024 bytes)
      const maxSize = 25 * 1024 * 1024; // 25MB to match server limit
      
      if (!allowedTypes.includes(file.type)) {
        setUploadError("Please select a PDF or DOCX file.");
        setUploadFile(null);
        event.target.value = "";
        // Clear form data if invalid file
        handleChange({
          target: {
            name: 'uploadedFile',
            value: null
          }
        });
        handleChange({
          target: {
            name: 'uploadedFileName',
            value: ""
          }
        });
      } else if (file.size > maxSize) {
        setUploadError(`File size too large. Maximum allowed size is ${(maxSize / (1024 * 1024)).toFixed(0)}MB. Your file is ${(file.size / (1024 * 1024)).toFixed(2)}MB.`);
        setUploadFile(null);
        event.target.value = "";
        // Clear form data if file too large
        handleChange({
          target: {
            name: 'uploadedFile',
            value: null
          }
        });
        handleChange({
          target: {
            name: 'uploadedFileName',
            value: ""
          }
        });
      } else {
        // Immediately update form data with valid file for validation
        handleChange({
          target: {
            name: 'uploadedFile',
            value: file
          }
        });
        handleChange({
          target: {
            name: 'uploadedFileName',
            value: file.name
          }
        });
      }
    } else {
      // Clear form data if no file selected
      handleChange({
        target: {
          name: 'uploadedFile',
          value: null
        }
      });
      handleChange({
        target: {
          name: 'uploadedFileName',
          value: ""
        }
      });
    }
  };

  /**
   * Process the uploaded document with automatic AI extraction
   */
  const handleProcessDocument = async () => {
    if (!uploadFile) {
      setUploadError("Please select a file to upload first.");
      return false; // Return false to indicate failure
    }

    try {
      setIsProcessing(true);
      setUploadError("");
      setProcessingStatus("Uploading document...");

      // Create FormData for file upload
      const formData = new FormData();
      formData.append('manuscript', uploadFile);

      setProcessingStatus("Extracting information using AI...");
      
      // Process document using AI
      const result = await processUploadedDocument(formData);
      
      setProcessingStatus("Processing complete!");
      
      // Get current user info (you may need to get this from context/props)
      const currentUser = {
        Name: formData.submitterFirstName || "",
        Surname: formData.submitterLastName || "",
        Affiliation: formData.submitterAffiliation || ""
      };

      // Ensure current user is included in authors
      let authors = result.extractedInfo?.authors || [];
      
      // Check if current user is already in the authors list
      const currentUserExists = authors.some(author => 
        author.Name === currentUser.Name && author.Surname === currentUser.Surname
      );
      
      // If current user is not in authors list, add them
      if (!currentUserExists && (currentUser.Name || currentUser.Surname)) {
        authors = [currentUser, ...authors];
      }
      
      // Call the callback with extracted information
      onExtractedInfo({
        extractedInfo: {
          ...result.extractedInfo,
          authors: authors
        },
        fileInfo: result.fileInfo,
        tempFileId: result.tempFileId,
        conversionStatus: result.conversionStatus
      });
      
      return true; // Return true to indicate success

    } catch (error) {
      console.error("Error processing document:", error);
      setUploadError(
        error.response?.data?.message || 
        "AI extraction failed. You can manually enter the information in the next steps."
      );
      
      // File info is already in form data from handleFileChange
      // No need to update it again here
      
      return false; // Return false to indicate extraction failed but file is uploaded
    } finally {
      setIsProcessing(false);
      setProcessingStatus("");
    }
  };

  return (
    <div className="row">
      <div className="col-12">
        <h4 className="mb-4">
          <i className="bi bi-cloud-upload me-2"></i>
          Manuscript Upload
        </h4>
        
        {/* File Upload Section */}
        <div className="mb-4">
          <label htmlFor="manuscriptFile" className="form-label fw-bold">
            File Upload <span className="text-danger">*</span>
          </label>
          <div className="position-relative">
            <input
              type="file"
              id="manuscriptFile"
              className="form-control opacity-0 position-absolute"
              accept=".pdf,.docx"
              onChange={handleFileChange}
              disabled={isProcessing}
              style={{ zIndex: 1 }}
            />
            <div 
              className="form-control d-flex align-items-center"
              style={{ 
                cursor: isProcessing ? 'not-allowed' : 'pointer',
                color: (uploadFile || formData.uploadedFileName) ? '#212529' : '#6c757d'
              }}
              onClick={() => !isProcessing && document.getElementById('manuscriptFile').click()}
            >
              {(uploadFile || formData.uploadedFileName) 
                ? (uploadFile ? uploadFile.name : formData.uploadedFileName)
                : 'Choose a File'
              }
            </div>
          </div>

          {(uploadFile || formData.uploadedFileName) && (
            <div className="mt-3">
              <div className="d-flex align-items-center bg-light p-3 rounded">
                <i className="bi bi-file-earmark-text me-2 text-primary"></i>
                <div className="flex-grow-1">
                  <div className="fw-semibold">
                    {uploadFile ? uploadFile.name : formData.uploadedFileName}
                  </div>
                  <div className="text-muted small">
                    {uploadFile ? 
                      `${(uploadFile.size / (1024 * 1024)).toFixed(2)} MB` : 
                      'File information available'
                    }
                  </div>
                </div>
                {hasExtractedInfo && (
                  <div className="badge bg-success">
                    <i className="bi bi-check-circle me-1"></i>
                    Processed
                  </div>
                )}
              </div>
              <div className="form-text mt-2">
                <i className="bi bi-info-circle me-1"></i>
                {hasExtractedInfo ? 
                  'File has been processed and information extracted successfully.' :
                  'File uploaded successfully. AI extraction will begin automatically when you proceed to the next step.'
                }
              </div>
            </div>
          )}

          {/* Processing Status */}
          {isProcessing && processingStatus && (
            <div className="mt-3">
              <div className="alert alert-info" role="alert">
                <div className="d-flex align-items-center">
                  <div className="spinner-border spinner-border-sm me-3" role="status">
                    <span className="visually-hidden">Processing...</span>
                  </div>
                  <span>{processingStatus}</span>
                </div>
              </div>
            </div>
          )}

          {/* Upload Error */}
          {uploadError && (
            <div className="alert alert-warning mt-3" role="alert">
              <i className="bi bi-exclamation-triangle me-2"></i>
              {uploadError}
            </div>
          )}
        </div>

        {/* Success Message and Extracted Info Preview */}
        {hasExtractedInfo && extractedInfo && (
          <div className="alert alert-success" role="alert">
            <h6 className="alert-heading">
              <i className="bi bi-check-circle me-2"></i>
              Document Processed Successfully!
            </h6>
            <p>
              We've extracted the following information from your document:
            </p>
            
            {/* Title */}
            <div className="mb-3">
              <strong>Title:</strong>
              <div className="ms-3 mt-1">
                {extractedInfo.extractedInfo?.title ? (
                  <span className="text-success">✓ {extractedInfo.extractedInfo.title}</span>
                ) : (
                  <span className="text-warning">⚠ Not extracted</span>
                )}
              </div>
            </div>
            
            {/* Authors */}
            <div className="mb-3">
              <strong>Authors ({extractedInfo.extractedInfo?.authors?.length || 0} found):</strong>
              <div className="ms-3 mt-1">
                {extractedInfo.extractedInfo?.authors && extractedInfo.extractedInfo.authors.length > 0 ? (
                  <div>
                    {extractedInfo.extractedInfo.authors.map((author, index) => (
                      <div key={index} className="text-success">
                        ✓ {author.Name || author.firstName || 'Unknown'} {author.Surname || author.lastName || ''}
                        {author.Affiliation || author.institution ? (
                          <span className="text-muted ms-2">({author.Affiliation || author.institution})</span>
                        ) : ''}
                      </div>
                    ))}
                  </div>
                ) : (
                  <span className="text-warning">⚠ No authors found</span>
                )}
              </div>
            </div>
            
            {/* Abstract */}
            <div className="mb-3">
              <strong>Abstract:</strong>
              <div className="ms-3 mt-1">
                {extractedInfo.extractedInfo?.abstract ? (
                  <div>
                    <span className="text-success">✓ Extracted</span>
                    <div className="mt-2 p-2 bg-light border-start border-3 border-success rounded">
                      <small className="text-muted">
                        {extractedInfo.extractedInfo.abstract.length > 200 
                          ? `${extractedInfo.extractedInfo.abstract.substring(0, 200)}...` 
                          : extractedInfo.extractedInfo.abstract
                        }
                      </small>
                    </div>
                  </div>
                ) : (
                  <span className="text-warning">⚠ Not found</span>
                )}
              </div>
            </div>

            {/* Additional extracted information */}
            {(extractedInfo.extractedInfo?.keywords || extractedInfo.extractedInfo?.references) && (
              <div className="mb-3">
                <strong>Additional Information:</strong>
                <div className="ms-3 mt-1">
                  {extractedInfo.extractedInfo.keywords && (
                    <div className="text-success">✓ Keywords extracted</div>
                  )}
                  {extractedInfo.extractedInfo.references && (
                    <div className="text-success">✓ References found</div>
                  )}
                </div>
              </div>
            )}
            
            <hr />
            <p className="mb-0">
              <i className="bi bi-info-circle me-1"></i>
              You can review and edit this information in the next steps of the submission process.
            </p>
          </div>
        )}

        {/* File Size Warning */}
        {/* <div className="alert alert-warning" role="alert">
          <i className="bi bi-exclamation-triangle me-2"></i>
          <strong>File Size Limit:</strong> Maximum file size is 25MB. If your manuscript is larger, 
          please compress the PDF or reduce image quality before uploading.
        </div> */}

        {/* Information Box */}
        <div className="alert alert-secondary" role="alert">
          <h6 className="alert-heading">
            <i className="bi bi-lightbulb me-2"></i>
            Notes:
          </h6>
          <ul className="mb-0">
            <li>PDF is preferred. DOCX may not convert cleanly to PDF.</li>
            <li>We extract the title, abstract, and author list from your file (PDFs only).</li>
            <li>Max size: 25 MB. If needed, compress the PDF or reduce image quality.</li>
          </ul>
        </div>


        {/* Required fields notice */}
        <div className="mb-3">
          <p className="text-muted">
            <span className="text-danger">*</span> Required
          </p>
        </div>

      </div>
    </div>
  );
};

export default PaperDocumentUpload;
